import { useSMANumbers } from '@core/logic/hooks/useSMANumbers';
import {
  useGetPointElementOrderNumbersQuery,
  useGetSplineElementOrderNumbersQuery,
} from '@core/redux/query';
import { Alert, Chip, Stack, Typography } from '@mui/material';
import { OrderNumber, OrderNumberType, isOrderNumberClosed } from '@projects/types/orderNumbers';
import { useGetSMAMappings } from '@projects/pages/detail/SmaNumbers/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { pointSelectors } from '@trenches/redux';
import { elementSelectors } from '@elements/redux';
import { pointElementSelectors } from '@elements/redux/point-element.slice';
import { scanSelectors } from '@scans/redux';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useSelectedElements } from './ElementSelect/connect';
import { doesLabeledSmaMismatchAnyScan, getScanIdsForLabeledElement } from './smaMismatch';

export const ElementSMADetails = () => {
  const { selectedPoints, selectedSplines, hasSelectedPoints, hasSelectedSplines } =
    useSelectedElements();
  const { data: pointElementOrderNumbers, isSuccess: pointOrderNumbersLoaded } =
    useGetPointElementOrderNumbersQuery(selectedPoints.at(0)?.id);
  const { data: splineElementOrderNumbers, isSuccess: splineOrderNumbersLoaded } =
    useGetSplineElementOrderNumbersQuery(selectedSplines.at(0)?.id);
  const { getSmaLabel } = useGetSMAMappings();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const allPoints = useSelector(pointSelectors.selectAll);
  const pointEntities = useMemo(
    () => Object.fromEntries(allPoints.map((point) => [point.id, point])),
    [allPoints],
  );
  const selectedPointElement = useSelector(
    pointElementSelectors.selectById(selectedPoints.at(0)?.id),
  );
  const selectedSplineElement = useSelector(elementSelectors.selectById(selectedSplines.at(0)?.id));
  const selectedElement = hasSelectedPoints ? selectedPointElement : selectedSplineElement;
  const scanIds = getScanIdsForLabeledElement(selectedElement, pointEntities);
  const scans = useSelector(scanSelectors.selectAll);
  const scansById = useMemo(
    () => Object.fromEntries(scans.map((scan) => [scan.id, scan])),
    [scans],
  );
  const { fetchSMANumbers } = useSMANumbers();
  const [scanOrderNumbersById, setScanOrderNumbersById] = useState<
    Record<string, OrderNumber[] | undefined>
  >({});
  const selectedOrderNumbers = hasSelectedPoints
    ? (pointElementOrderNumbers ?? [])
    : (splineElementOrderNumbers ?? []);
  const elementOrderNumbersLoaded = hasSelectedPoints
    ? pointOrderNumbersLoaded
    : splineOrderNumbersLoaded;
  const labeledSmaNumbers = selectedOrderNumbers.filter(({ type }) => type === OrderNumberType.SMA);

  useEffect(() => {
    let isMounted = true;
    setScanOrderNumbersById({});

    if (projectId && scanIds.length > 0) {
      Promise.all(
        scanIds.map(async (scanId) => {
          const scan = scansById[scanId];
          if (scan?.orderNumbers) {
            return [scanId, scan.orderNumbers] as const;
          }

          try {
            return [scanId, await fetchSMANumbers(projectId, scanId, 'scans')] as const;
          } catch (error) {
            console.error(`Failed to fetch SMA numbers for associated scan ${scanId}:`, error);
            return [scanId, undefined] as const;
          }
        }),
      ).then((results) => {
        if (isMounted) setScanOrderNumbersById(Object.fromEntries(results));
      });
    }

    return () => {
      isMounted = false;
    };
  }, [fetchSMANumbers, projectId, scanIds.join(','), scansById]);

  const showSmaMismatchWarning =
    elementOrderNumbersLoaded &&
    scanIds.every((scanId) => scanOrderNumbersById[scanId] !== undefined) &&
    doesLabeledSmaMismatchAnyScan(
      scanIds.map((scanId) => ({
        orderNumbers: scanOrderNumbersById[scanId] ?? [],
        hasSmaInformation:
          scansById[scanId]?.orderNumbers !== undefined ||
          (scanOrderNumbersById[scanId]?.length ?? 0) > 0,
      })),
      labeledSmaNumbers,
    );

  if (!hasSelectedPoints && !hasSelectedSplines) {
    return null;
  }

  return (
    <Stack p={2} spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline">SMA</Typography>
      </Stack>

      {showSmaMismatchWarning && (
        <Alert severity="warning">
          The SMA numbers assigned to this labeled data differ from its associated scan.
        </Alert>
      )}

      <Stack direction="row" flexWrap="wrap" gap={1}>
        {hasSelectedPoints &&
          pointElementOrderNumbers?.map((smaInfo) => {
            const closed = isOrderNumberClosed(smaInfo);
            const label = `${getSmaLabel(smaInfo.value)}${closed ? ' (Closed)' : ''}`;
            return (
              <Chip
                label={label}
                key={smaInfo.value}
                variant="outlined"
                sx={(theme) => ({
                  color: closed ? theme.palette.text.disabled : theme.palette.text.primary,
                  borderColor: closed ? theme.palette.text.disabled : theme.palette.divider,
                  backgroundColor: closed ? theme.palette.action.hover : 'transparent',
                  opacity: closed ? 0.7 : 1,
                })}
              />
            );
          })}
        {hasSelectedSplines &&
          splineElementOrderNumbers?.map((smaInfo) => {
            const closed = isOrderNumberClosed(smaInfo);
            const label = `${getSmaLabel(smaInfo.value)}${closed ? ' (Closed)' : ''}`;
            return (
              <Chip
                label={label}
                key={smaInfo.value}
                variant="outlined"
                sx={(theme) => ({
                  color: closed ? theme.palette.text.disabled : theme.palette.text.primary,
                  borderColor: closed ? theme.palette.text.disabled : theme.palette.divider,
                  backgroundColor: closed ? theme.palette.action.hover : 'transparent',
                  opacity: closed ? 0.7 : 1,
                })}
              />
            );
          })}
      </Stack>
    </Stack>
  );
};
